import { z } from 'zod';
import { isRealIsoDate } from '../utils';

// ═══════════════════════════════════════════════════════════════
// ONBOARDING PERSONA MORAL (PM) — Representante legal + beneficiarios
// controladores (F5, plan persona-moral). Los shapes siguen exactamente el
// contrato del adapter `clientApi` (onboardingLegalRepresentatives /
// onboardingBeneficialOwners). NO agregar campos que el BE no persiste.
// ═══════════════════════════════════════════════════════════════

// El usuario captura la fecha con `DateInput` (DD/MM/AAAA); el form la convierte
// a ISO `YYYY-MM-DD` (lo que el BE espera) antes de llegar aquí. El mensaje se
// redacta en el formato que el usuario ve (DD/MM/AAAA), no en el ISO interno.
const INVALID_DATE_MESSAGE = 'Ingresa una fecha válida (DD/MM/AAAA)';

/**
 * Fecha ISO opcional. Un `DateInput` vacío entrega `''` (no `undefined`), así
 * que aceptamos string vacío y lo normalizamos a `undefined` para que el
 * adapter lo mande como `null`. Si trae contenido, debe ser una fecha
 * calendario real (rechaza `2020-13-40`).
 */
const optionalIsoDateSchema = z
  .string()
  .trim()
  .refine(v => v === '' || isRealIsoDate(v), INVALID_DATE_MESSAGE)
  .optional()
  .transform(v => (v === '' ? undefined : v));

// ───────────────────────────────────────────────────────────────
// Representantes legales (screen 18): array + política de firma.
// El BE usa `last_name` único: el form captura paterno y materno por separado
// (diseño) y el adapter los concatena.
// ───────────────────────────────────────────────────────────────

export const signaturePolicySchema = z.enum(['solo', 'mancomunado', 'solidario']);
/**
 * Tipos de poder notarial que acepta el BE. Los valores son los del catálogo
 * notarial mexicano, no una clasificación genérica: un mismo representante
 * puede tener poder para actos de administración pero no de dominio.
 */
export const powerTypeSchema = z.enum([
  'administracion',
  'dominio',
  'pleitos_y_cobranzas',
  'actos_especiales',
]);

export const legalRepresentativeSchema = z
  .object({
    /** Si el representante ya es usuario PF del tenant (→ `user_id_existing`). */
    userIdExisting: z.string().trim().optional(),
    firstName: z.string().trim().min(1, 'El nombre es requerido'),
    /** Apellido paterno (→ primera mitad de `last_name`). */
    lastNamePaternal: z.string().trim().min(1, 'El apellido paterno es requerido'),
    /** Apellido materno (→ segunda mitad de `last_name`). Opcional: no todas
     *  las personas tienen dos apellidos. */
    lastNameMaternal: z.string().trim().optional(),
    /**
     * Identidad del representante que hace el onboarding (RFC 0030 §13.1). El BE
     * la persiste en su perfil de persona física y con ella cierra la
     * confirmación de datos del KYC. Opcionales: lo que falte lo toma del OCR.
     */
    secondName: z.string().trim().optional(),
    /** `DD/MM/AAAA` (formato del DateInput); el BE acepta ese formato o ISO. */
    birthDate: z.string().trim().optional(),
    curp: z.string().trim().optional(),
    rfc: z.string().trim().optional(),
    nationality: z.string().trim().optional(),
    /**
     * Correo y tipo de poder salieron de la pantalla (2026-08-03, diseño): ya no
     * se capturan, así que dejan de ser requeridos aquí. Siguen en el schema
     * porque el BE los espera en el body — ver nota en el adapter.
     */
    email: z.string().trim().email('El correo no es válido').optional(),
    powerType: powerTypeSchema.optional(),
    /**
     * Fecha en que se otorgó el poder. **Opcional**: el BE usa la fecha actual
     * cuando no se manda, así que exigirla en el form bloqueaba un envío que el
     * servidor acepta.
     */
    powerGrantedAt: optionalIsoDateSchema,
    /** Fecha de expiración del poder (opcional: poderes sin vencimiento). */
    powerExpiresAt: optionalIsoDateSchema,
  })
  // Si hay ambas, la expiración debe ser posterior al otorgamiento (comparación
  // léxica de ISO `YYYY-MM-DD` = cronológica). Sin otorgamiento no hay nada que
  // comparar: el BE pondrá la fecha actual.
  .refine(
    rep => !rep.powerExpiresAt || !rep.powerGrantedAt || rep.powerExpiresAt > rep.powerGrantedAt,
    {
      message: 'La expiración del poder debe ser posterior a su otorgamiento',
      path: ['powerExpiresAt'],
    }
  );

export const legalRepresentativesFormSchema = z
  .object({
    signaturePolicy: signaturePolicySchema,
    representatives: z
      .array(legalRepresentativeSchema)
      .min(1, 'Declara al menos un representante legal'),
  })
  // Firma mancomunada o solidaria significa que firman varios: con un solo
  // representante declarado la política no tiene sentido y el BE la rechaza.
  .refine(data => data.signaturePolicy === 'solo' || data.representatives.length >= 2, {
    message: 'La firma mancomunada o solidaria requiere al menos dos representantes',
    path: ['signaturePolicy'],
  });

// ───────────────────────────────────────────────────────────────
// Beneficiarios controladores (LFPIORPI Art. 17, OBLIGATORIO).
// ───────────────────────────────────────────────────────────────

/** Umbral de participación para considerar a alguien beneficiario controlador. */
export const MIN_BENEFICIAL_OWNERSHIP_PERCENTAGE = 25;

export const beneficialOwnerSubjectTypeSchema = z.enum(['pf', 'pm']);
/**
 * Formas de control sobre la empresa, con los valores EXACTOS que traduce el
 * `CONTROL_TYPE_MAP` del BE.
 *
 * ⚠️ El BE mapea cualquier valor desconocido a `direct_ownership` **sin
 * fallar** (`CONTROL_TYPE_MAP[raw] || 'direct_ownership'`). El catálogo previo
 * del FE (`shareholder` / `director` / `controller` / `other`) no intersecaba
 * con ese mapa, así que todo beneficiario declarado quedaba archivado como
 * propiedad directa aunque el usuario hubiera elegido otra cosa — un dato
 * regulatorio (LFPIORPI Art. 17) corrupto en silencio. No cambiar estos
 * valores sin confirmar el mapa del BE.
 */
export const beneficialOwnerControlTypeSchema = z.enum([
  'equity',
  'direct_ownership',
  'indirect_ownership',
  'voting',
  'voting_rights',
  'contract',
  'control_by_other_means',
]);

/**
 * RFC (12-13) o CURP (18). El BE acepta cualquiera de los dos en el mismo
 * campo (`rfc_or_curp`); validamos formato laxo para no rechazar identificadores
 * válidos. La validación canónica del dígito/estructura es server-side.
 */
const rfcOrCurpSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(
    /^([A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}|[A-Z][AEIOUX][A-Z]{2}\d{6}[HM][A-Z]{5}[A-Z0-9]\d)$/,
    'Ingresa un RFC (12-13) o CURP (18) válido'
  );

export const beneficialOwnerSchema = z.object({
  subjectType: beneficialOwnerSubjectTypeSchema,
  /** Si el beneficiario ya existe como sujeto en el tenant. */
  existingSubjectId: z.string().trim().optional(),
  name: z.string().trim().min(1, 'El nombre es requerido'),
  rfcOrCurp: rfcOrCurpSchema,
  /**
   * Participación mínima para calificar como beneficiario controlador: el BE
   * rechaza por debajo de 25% (VAL-001 "Ownership percentage must be at least
   * 25.0% for beneficial owner classification"). Es el umbral de LFPIORPI: por
   * debajo de eso la persona no se considera controladora y no debe declararse
   * en este paso.
   */
  ownershipPercentage: z
    .number({ invalid_type_error: 'El porcentaje es requerido' })
    .min(MIN_BENEFICIAL_OWNERSHIP_PERCENTAGE, 'La participación mínima es 25%')
    .max(100, 'El porcentaje no puede ser mayor a 100'),
  controlType: beneficialOwnerControlTypeSchema,
  relationship: z.string().trim().optional(),
});

export const beneficialOwnersFormSchema = z
  .object({
    owners: z.array(beneficialOwnerSchema).min(1, 'Declara al menos un beneficiario controlador'),
  })
  .refine(data => data.owners.reduce((sum, o) => sum + o.ownershipPercentage, 0) <= 100, {
    message: 'La suma de participación no puede exceder 100%',
    path: ['owners'],
  });

// ───────────────────────────────────────────────────────────────
// Reuso de identidad PF→PM (attach-kyc-from-existing).
// ⚠️ El body exacto NO está confirmado contra swagger BE — ver adapter.
// ───────────────────────────────────────────────────────────────

export const attachKycConsentSchema = z.object({
  consentAccepted: z.literal(true, {
    errorMap: () => ({ message: 'Debes aceptar para reusar tu identidad' }),
  }),
  sourceSessionId: z.string().trim().optional(),
});

// ═══════════════════════════════════════════════════════════════
// Tipos inferidos
// ═══════════════════════════════════════════════════════════════

export type SignaturePolicy = z.infer<typeof signaturePolicySchema>;
export type PowerType = z.infer<typeof powerTypeSchema>;
export type LegalRepresentativeFormData = z.infer<typeof legalRepresentativeSchema>;
export type LegalRepresentativesFormData = z.infer<typeof legalRepresentativesFormSchema>;
export type BeneficialOwnerSubjectType = z.infer<typeof beneficialOwnerSubjectTypeSchema>;
export type BeneficialOwnerControlType = z.infer<typeof beneficialOwnerControlTypeSchema>;
export type BeneficialOwnerFormData = z.infer<typeof beneficialOwnerSchema>;
export type BeneficialOwnersFormData = z.infer<typeof beneficialOwnersFormSchema>;
export type AttachKycConsentData = z.infer<typeof attachKycConsentSchema>;
